import { type ReactNode, useEffect, useRef, useState } from 'react'
import './InfiniteTicker.css'

type InfiniteTickerProps = {
  children: ReactNode
  className?: string
  groupClassName?: string
  direction?: 'left' | 'right'
  duration?: number
}

export default function InfiniteTicker({
  children,
  className = '',
  groupClassName = '',
  direction = 'left',
  duration = 30,
}: InfiniteTickerProps) {
  const tickerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const groupRef = useRef<HTMLDivElement>(null)
  const [copyCount, setCopyCount] = useState(24)
  const [shift, setShift] = useState(0)

  useEffect(() => {
    const ticker = tickerRef.current
    const group = groupRef.current
    if (!ticker || !group) return

    const update = () => {
      const groupWidth = group.scrollWidth
      if (!groupWidth) return

      setShift(groupWidth)
      setCopyCount(Math.max(3, Math.ceil((ticker.offsetWidth + groupWidth) / groupWidth) + 1))
    }

    update()

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(ticker)
    resizeObserver.observe(group)

    if (document.fonts?.ready) {
      document.fonts.ready.then(update).catch(() => {})
    }

    return () => resizeObserver.disconnect()
  }, [children])

  useEffect(() => {
    const track = trackRef.current
    if (!track || !shift) return

    const baseDirection = direction === 'left' ? 1 : -1
    const baseSpeed = shift / duration
    const targetDirection = { current: baseDirection }
    const targetBoost = { current: 0 }
    const currentDirection = { current: baseDirection }
    const currentBoost = { current: 0 }
    const offset = { current: 0 }
    const lastScrollY = { current: window.scrollY }
    const lastScrollAt = { current: 0 }
    let lastFrame = performance.now()
    let frameId = 0

    const wrapOffset = (value: number) => ((value % shift) + shift) % shift

    const handleScroll = () => {
      const now = performance.now()
      const y = window.scrollY
      const delta = y - lastScrollY.current
      const elapsed = Math.max(16, now - (lastScrollAt.current || now - 16))

      lastScrollY.current = y
      lastScrollAt.current = now

      if (Math.abs(delta) < 0.5) return

      targetDirection.current = delta > 0 ? baseDirection : -baseDirection
      targetBoost.current = Math.min(1.15, Math.abs(delta) / elapsed * 0.55)
    }

    const tick = (now: number) => {
      const elapsed = Math.min(64, now - lastFrame) / 1000
      lastFrame = now

      if (now - lastScrollAt.current > 180) {
        targetDirection.current = baseDirection
        targetBoost.current = 0
      }

      currentDirection.current += (targetDirection.current - currentDirection.current) * 0.08
      currentBoost.current += (targetBoost.current - currentBoost.current) * 0.08
      offset.current = wrapOffset(
        offset.current + baseSpeed * (1 + currentBoost.current) * currentDirection.current * elapsed,
      )

      track.style.transform = `translate3d(${-offset.current}px, 0, 0)`
      frameId = requestAnimationFrame(tick)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    frameId = requestAnimationFrame(tick)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(frameId)
    }
  }, [direction, duration, shift])

  return (
    <div
      ref={tickerRef}
      className={`infinite-ticker ${className}`.trim()}
      data-direction={direction}
    >
      <div ref={trackRef} className="infinite-ticker-track">
        {Array.from({ length: copyCount }).map((_, index) => (
          <div
            ref={index === 0 ? groupRef : undefined}
            className={`infinite-ticker-group ${groupClassName}`.trim()}
            key={index}
            aria-hidden={index > 0 ? 'true' : undefined}
          >
            {children}
          </div>
        ))}
      </div>
    </div>
  )
}
